"use client";

import * as React from "react";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { Pagination, PaginationContent, PaginationItem, PaginationLink } from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const filters = [{
  label: "ALL",
  value: "all"
}, {
  label: "OFFICE",
  value: "office"
}, {
  label: "SEATING",
  value: "seating"
}, {
  label: "LIGHTING",
  value: "lighting"
}, {
  label: "STORAGE",
  value: "storage"
}];
function ProductsCatalog() {
  const {
    products,
    loading,
    error,
    refetch,
    setSorting,
    currentPage,
    totalPages,
    setPage,
    total
  } = useEcommerceProducts();
  const [activeFilter, setActiveFilter] = React.useState("all");
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedProduct, setSelectedProduct] = React.useState<any>(null);
  const handleSort = (value: string) => {
    if (value === "newest") setSorting("date", "desc");else if (value === "oldest") setSorting("date", "asc");else if (value === "price-asc") setSorting("price", "asc");else if (value === "price-desc") setSorting("price", "desc");else if (value === "name") setSorting("title", "asc");
  };
  const filteredProducts = products.filter(p => {
    const matchesFilter = activeFilter === "all" || p.categories?.some(c => c.slug === activeFilter);
    const matchesSearch = !searchTerm || p.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });
  return <>
      {}
      <div className="border-y border-primary-200">
        <div className="flex flex-col gap-4 py-5 md:flex-row md:items-center md:justify-between">
          <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
            <div className="flex items-center gap-2 md:flex-wrap">
              {filters.map((filter, i) => <Button key={filter.value} data-index={i} variant="ghost" onClick={() => setActiveFilter(filter.value)} className={`shrink-0 h-auto px-4 py-2 text-[11px] tracking-[0.2em] font-default border rounded-md transition-colors ${activeFilter === filter.value ? "bg-primary-900 text-white border-primary-900 hover:bg-primary-800" : "border-primary-200 text-primary-900/70 hover:border-primary-500 hover:text-primary-900 hover:bg-transparent"}`}>
                  {filter.label}
                </Button>)}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Select onValueChange={handleSort} defaultValue="newest">
              <SelectTrigger className="h-9 border-primary-200 bg-transparent text-[11px] tracking-[0.2em] font-default uppercase">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
                <SelectItem value="price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price-desc">Price: High to Low</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(v => !v)} aria-label="Search products" className="h-9 w-9 border border-primary-200 text-primary-900/80 hover:bg-primary-900 hover:text-white rounded-md">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {searchOpen && <div className="pb-5">
            <Input autoFocus type="search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search our inventory..." className="border-primary-200 bg-transparent font-default text-base" />
          </div>}
      </div>

      {}
      <div className="mt-12">
        {loading ? <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
            {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="aspect-square bg-primary-200/20 animate-pulse" />)}
          </div> : error ? <div className="py-16 text-center">
            <p className="text-sm text-primary-500 font-default tracking-wider">
              UNABLE TO LOAD INVENTORY
            </p>
            <Button onClick={refetch} variant="ghost" className="mt-4 text-xs tracking-[0.2em] font-default uppercase underline">
              Retry
            </Button>
          </div> : filteredProducts.length === 0 ? <div className="py-24 text-center">
            <p className="font-default text-2xl text-primary-900/70">
              No items found.
            </p>
            <p className="mt-2 text-[11px] tracking-[0.2em] font-default uppercase text-primary-500">
              Try a different filter
            </p>
          </div> : <div className="grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8 lg:grid-cols-4">
            {filteredProducts.map((product, i) => <article key={product.id} data-index={i} className="group relative">
                <div className="relative overflow-hidden bg-white rounded-lg shadow-sm transition-transform duration-500 group-hover:-translate-y-1">
                  <Link to={product.link}>
                    <div className="relative aspect-square overflow-hidden p-4 md:p-6">
                      {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" /> : <div className="flex h-full w-full items-center justify-center bg-primary-100">
                          <span className="font-default text-4xl text-primary-300">
                            Item
                          </span>
                        </div>}
                    </div>
                  </Link>

                  {}
                  <div className="pointer-events-none absolute inset-x-4 bottom-4 translate-y-4 opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 md:inset-x-6 md:bottom-6">
                    <Button onClick={() => setSelectedProduct(product)} className="w-full bg-primary-500 text-white text-[11px] tracking-[0.25em] font-default uppercase hover:bg-primary-600 rounded-md">
                      Quick View
                    </Button>
                  </div>
                </div>

                <div className="mt-4 px-1">
                  {product.categories && product.categories.length > 0 && <p className="text-[10px] tracking-[0.25em] font-default uppercase text-primary-500">
                      {product.categories[0].name}
                    </p>}
                  <Link to={product.link}>
                    <h3 className="mt-2 font-default text-base leading-snug text-primary-900 line-clamp-2 transition-colors hover:text-primary-500 md:text-lg">
                      {product.title}
                    </h3>
                  </Link>
                  <p className="mt-2 text-[11px] tracking-[0.2em] font-default uppercase text-primary-600">
                    {product.discounted_price ? <>
                        <span>${product.discounted_price.toFixed(2)}</span>
                        <span className="ml-2 text-primary-300 line-through">
                          ${product.price.toFixed(2)}
                        </span>
                      </> : <span>${product.price?.toFixed(2)}</span>}
                  </p>
                </div>
              </article>)}
          </div>}

        {}
        {totalPages > 1 && <div className="mt-16 flex justify-center">
            <Pagination>
              <PaginationContent className="gap-3">
                {Array.from({
              length: totalPages
            }).map((_, i) => {
              const page = i + 1;
              const isActive = page === currentPage;
              return <React.Fragment key={page}>
                      <PaginationItem>
                        <PaginationLink isActive={isActive} onClick={e => {
                    e.preventDefault();
                    setPage(page);
                  }} className={`h-auto w-auto rounded-md border-0 bg-transparent px-1 text-sm font-default tracking-wider ${isActive ? "text-primary-900 underline underline-offset-8" : "text-primary-400 hover:text-primary-900"}`}>
                          {String(page).padStart(2, "0")}
                        </PaginationLink>
                      </PaginationItem>
                      {page < totalPages && <span className="self-center text-primary-200">·</span>}
                    </React.Fragment>;
            })}
              </PaginationContent>
            </Pagination>
          </div>}

        {total > 0 && <p className="mt-8 text-center text-[10px] tracking-[0.25em] font-default uppercase text-primary-400">
            Showing {filteredProducts.length} of {total} items
          </p>}
      </div>

      {}
      <Dialog open={!!selectedProduct} onOpenChange={open => !open && setSelectedProduct(null)}>
        <DialogContent showCloseButton={false} className="max-w-4xl border-primary-200 bg-white p-0 rounded-lg">
          {selectedProduct && <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative aspect-square bg-primary-100 p-6 md:p-10">
                {selectedProduct.featured_image_url ? <img src={selectedProduct.featured_image_url} alt={selectedProduct.title} loading="lazy" className="h-full w-full object-cover rounded-md" /> : <div className="flex h-full w-full items-center justify-center bg-primary-200">
                    <span className="font-default text-6xl text-primary-300">
                      Item
                    </span>
                  </div>}
              </div>

              <div className="flex flex-col p-6 md:p-10">
                <div className="flex items-start justify-between">
                  {selectedProduct.categories && selectedProduct.categories.length > 0 ? <p className="text-[10px] tracking-[0.3em] font-default uppercase text-primary-500">
                      {selectedProduct.categories[0].name}
                    </p> : <span />}
                  <Button variant="ghost" size="icon" onClick={() => setSelectedProduct(null)} className="h-8 w-8 text-primary-400 hover:text-primary-900 hover:bg-transparent rounded-md" aria-label="Close">
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <DialogHeader className="mt-4">
                  <DialogTitle className="font-default text-2xl leading-tight text-primary-900 md:text-3xl">
                    {selectedProduct.title}
                  </DialogTitle>
                </DialogHeader>

                <p className="mt-4 text-sm tracking-[0.2em] font-default uppercase text-primary-600">
                  {selectedProduct.discounted_price ? <>
                      <span>${selectedProduct.discounted_price.toFixed(2)}</span>
                      <span className="ml-2 text-primary-300 line-through">
                        ${selectedProduct.price.toFixed(2)}
                      </span>
                    </> : <span>${selectedProduct.price?.toFixed(2)}</span>}
                </p>

                {selectedProduct.short_description && <div className="mt-6 font-default text-sm leading-relaxed text-primary-800 [&_p]:mb-2" dangerouslySetInnerHTML={{
              __html: selectedProduct.short_description
            }} />}

                <div className="mt-6 space-y-3 border-t border-primary-200 pt-6">
                  <div className="flex justify-between text-[11px] tracking-[0.2em] font-default uppercase">
                    <span className="text-primary-400">Material</span>
                    <span className="text-primary-900">
                      {selectedProduct.customFields?.material || "Premium Grade"}
                    </span>
                  </div>
                  <div className="flex justify-between text-[11px] tracking-[0.2em] font-default uppercase">
                    <span className="text-primary-400">Dimensions</span>
                    <span className="text-primary-900">
                      {selectedProduct.customFields?.dimensions || "Standard"}
                    </span>
                  </div>
                  <div className="flex justify-between text-[11px] tracking-[0.2em] font-default uppercase">
                    <span className="text-primary-400">Origin</span>
                    <span className="text-primary-900">Global</span>
                  </div>
                </div>

                <div className="mt-auto pt-8">
                  <AddToCartButton productId={selectedProduct.id} className="w-full" buttonClassName="bg-primary-500 text-white w-full rounded-md h-12 text-[11px] tracking-[0.3em] font-default uppercase" text="Inquire / Reserve" showQuantity={true} />
                  <Link to={selectedProduct.link} className="mt-4 block text-center text-[11px] tracking-[0.25em] font-default uppercase text-primary-400 underline underline-offset-4 hover:text-primary-900">
                    View Full Details
                  </Link>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </>;
}
export default function Inventory() {
  const wp_query = new WP_Query({
    per_page: 12,
    order: "desc",
    orderby: "date"
  });
  return <section data-section-id="3534" id="available-inventory" className="bg-primary-100 text-primary-900 py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        {}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-7">
            <div className="flex gap-6 md:gap-10">
              <div className="hidden shrink-0 md:block" style={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)"
            }}>
                <p className="text-[10px] tracking-[0.4em] font-default uppercase text-primary-500">
                  The Inventory — Selection
                </p>
              </div>

              <div className="flex-1">
                <p className="text-[10px] tracking-[0.4em] font-default uppercase text-primary-500 md:hidden">
                  The Inventory — Selection
                </p>
                <h2 className="mt-4 font-default text-4xl leading-[1.05] tracking-tight text-primary-900 md:mt-0 md:text-6xl lg:text-7xl">
                  Premium office,
                  <br />
                  <span className="italic text-primary-900/85">refined for you.</span>
                </h2>
              </div>
            </div>
          </div>

          <div className="md:col-span-5 md:pt-4">
            <p className="font-default text-base leading-relaxed text-primary-900/75 md:text-lg">
              A curated selection of professional office solutions — ergonomic seating, 
              executive desks, and modern lighting designed to elevate your workspace.
            </p>
            <div className="mt-8 flex items-baseline gap-6 border-t border-primary-200 pt-4">
              <div>
                <p className="font-default text-3xl text-primary-900">48</p>
                <p className="mt-1 text-[10px] tracking-[0.3em] font-default uppercase text-primary-500">
                  Items
                </p>
              </div>
              <div className="h-10 w-px bg-primary-200" />
              <div>
                <p className="font-default text-3xl text-primary-900">04</p>
                <p className="mt-1 text-[10px] tracking-[0.3em] font-default uppercase text-primary-500">
                  Categories
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 md:mt-16">
          <EcommerceProductsProvider wp_query={{
          per_page: 12,
          order: "desc",
          orderby: "date"
        }}>
            <ProductsCatalog />
          </EcommerceProductsProvider>
        </div>
      </div>
    </section>;
}